import React, { useEffect, useMemo } from 'react';
import { Provider, useSelector } from 'react-redux';
import store, { useAppDispatch } from '@/store';
import { useTrackProductViewed } from '@/features/pdp/hooks/useTrackProductViewed';
import { setView } from '@/actions/pdp.actions';
import LoadingSpinner from '@/components/tailwind/LoadingSpinner';
import { getPDPV2Data } from '@/features/pdp/pdp-v2.data';
import { useSite } from '@hooks';
import { PDPViews, PDP_V2_ITEMS } from '@types';
import {lazyRetry} from "@utils/lazyRetry";
import { PDPActionTypes } from '@/constants/pdp.constants';
import usePageTitle from '@hooks/usePageTitle';
import parseJson from '@utils/parseJson';

const ProductDetails = React.lazy(() =>
    lazyRetry(
        () => import(/* webpackChunkName: "ProductDetails" */ '@/features/pdp/components/ProductDetails'),
        'ProductDetails'
    )
);

const ProductWithBundle = React.lazy(() =>
    lazyRetry(
        () => import(/* webpackChunkName: "ProductWithBundle" */ '@/features/pdp/components/ProductWithBundle'),
        'ProductWithBundle'
    )
);

const PDPRoot = ({ klaviyoConfigured, crumbs, site, jsonItem }) => {

    const dispatch = useAppDispatch();

    const pdpItem = useSelector((state: any) => state.pdpItem);
    const { details, inlineBundleData, view } = pdpItem;

    useSite(site);

    useEffect(() => {
      dispatch({ type: PDPActionTypes.SUCCESS, payload: jsonItem });
    }, [ jsonItem ]);

    const parsedCrumbs = useMemo(
        () =>
            crumbs &&
            (parseJson(crumbs) || []).map((crumb) => {
                return {
                    text: crumb.text,
                    url: crumb.link,
                    isInternalLink: true,
                };
            }),
        [crumbs]
    );

    useTrackProductViewed(details, site);

    useEffect(() => {
        const handleUrlChange = () => {
            const searchParams = new URLSearchParams(window.location.search);
            const customQueryParam = searchParams.get('custom');
            const designQueryParam = searchParams.get('design');
            const viewQueryParam = searchParams.get('view');

            if (!!customQueryParam) {
                dispatch(setView(PDPViews.BULK));
            } else if (!!designQueryParam) {
                dispatch(setView(PDPViews.DESIGN));
            } else if (!!viewQueryParam) {
                if (viewQueryParam === PDPViews.DESIGN) dispatch(setView(PDPViews.DESIGN));
                else if (viewQueryParam === PDPViews.BULK) dispatch(setView(PDPViews.BULK));
                else if (viewQueryParam === PDPViews.ASSET_PREVIEW) dispatch(setView(PDPViews.ASSET_PREVIEW));
                else if (viewQueryParam === PDPViews.DEFAULT) dispatch(setView(PDPViews.DEFAULT));
            } else {
                dispatch(setView(PDPViews.DEFAULT));
            }
        };

        window.addEventListener('popstate', handleUrlChange);

        handleUrlChange();

        return () => {
            window.removeEventListener('popstate', handleUrlChange);
        };
    }, [dispatch]);

    // console.log({ details, jsonItem })

    usePageTitle(details?.pageTitle);

    return (
      <div id="product-details-container" className="product-page tw-min-h-[150vh]">
        <React.Suspense fallback={<LoadingSpinner />}>
          {inlineBundleData ? (
            <ProductWithBundle
              klaviyoConfigured={klaviyoConfigured}
              v2Data={getPDPV2Data(details?.attributes?.typeId)}
              inlineBundleData={inlineBundleData}
              shopItemId={details.id}
              isV2={PDP_V2_ITEMS.includes(details?.attributes?.typeId)}
              crumbs={parsedCrumbs}
            />
          ) : (
            <ProductDetails
              klaviyoConfigured={klaviyoConfigured}
              v2Data={getPDPV2Data(details?.attributes?.typeId)}
              isV2={PDP_V2_ITEMS.includes(details?.attributes?.typeId)}
              crumbs={parsedCrumbs}
            />
          )}
        </React.Suspense>
      </div>
    );
};

export default PDPRoot;
